// src/utils/http.js
import router from '@/router'
import { Message } from 'element-ui'

const BASE_URL = ''

// 防止多个请求同时 401 时重复跳转登录页
let isRedirecting = false

/** 登录成功后重置跳转标记（由 Login.vue 调用） */
function resetRedirectFlag() {
  isRedirecting = false
}

/**
 * 从 JWT token 中解析过期时间（不验证签名）
 * @returns {number|null} 过期时间戳（ms），解析失败返回 null
 */
function getTokenExpiry(token) {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    const payload = JSON.parse(json)
    return payload.exp ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

async function request(url, options = {}) {
  const token = localStorage.getItem('token')

  // 请求前检查 token 是否即将过期（提前5分钟检测）
  if (token && !isRedirecting) {
    const expiry = getTokenExpiry(token)
    if (expiry && Date.now() >= expiry - 5 * 60 * 1000) {
      localStorage.removeItem('token')
      localStorage.removeItem('role')
      localStorage.removeItem('status')
      localStorage.removeItem('username')
      isRedirecting = true
      Message.warning('登录已过期，请重新登录')
      router.push('/login')
      return { code: 401, msg: '登录已过期' }
    }
  }

  // 去掉 /api 前缀
  const cleanUrl = url.replace(/^\/api/, '')

  const headers = {
    'Content-Type': 'application/json',
    ...(token && { token })
  }

  const response = await fetch(`${BASE_URL}${cleanUrl}`, {
    headers,
    ...options
  })

  if (response.status === 401) {
    if (!isRedirecting) {
      localStorage.removeItem('token')
      localStorage.removeItem('role')
      localStorage.removeItem('status')
      localStorage.removeItem('username')
      isRedirecting = true
      Message.warning('登录已过期，请重新登录')
      router.push('/login')
    }
    return { code: 401, msg: '登录已过期' }
  }

  const result = await response.json()

  if (result.code === 200 || result.code === 1) {
    return result
  } else {
    throw new Error(result.msg || `请求失败: ${result.code}`)
  }
}

/** 从接口响应中提取可展示的成功/提示文案（忽略对象类型的 data） */
export function getResponseMessage(result, fallback = '') {
  if (!result) return fallback
  const msg = result.msg ?? result.message
  if (typeof msg === 'string' && msg.trim()) return msg.trim()
  if (typeof result.data === 'string' && result.data.trim()) return result.data.trim()
  return fallback
}

export async function get(url, params = {}) {
  const queryString = new URLSearchParams(params).toString()
  const fullUrl = queryString ? `${url}?${queryString}` : url
  return request(fullUrl, { method: 'GET' })
}

export async function post(url, data = {}, params = {}) {
  const queryString = new URLSearchParams(params).toString()
  const fullUrl = queryString ? `${url}?${queryString}` : url
  return request(fullUrl, {
    method: 'POST',
    body: JSON.stringify(data)
  })
}

export async function put(url, data = {}) {
  return request(url, {
    method: 'PUT',
    body: JSON.stringify(data)
  })
}

export async function del(url) {
  return request(url, { method: 'DELETE' })
}

// 文件上传
export async function upload(file, path = '') {
  const token = localStorage.getItem('token')
  const formData = new FormData()
  formData.append('image', file)
  if (path) formData.append('path', path)

  const response = await fetch(`${BASE_URL}/common/upload`, {
    method: 'POST',
    headers: { ...(token && { token }) },
    body: formData
  })

  const result = await response.json()
  if (result.code === 200 || result.code === 1) {
    return result
  } else {
    throw new Error(result.msg || '上传失败')
  }
}

export { BASE_URL }
export { request }
export { resetRedirectFlag }

/**
 * 通用文件上传(对接后端 /api/admin/file/upload 接口,使用阿里云OSS)
 * @param {File} file - 要上传的文件
 * @returns {Promise<string>} 返回文件访问URL路径(OSS完整URL)
 */
export async function uploadFile(file) {
  const token = localStorage.getItem('token')

  if (!file || !file.size) {
    throw new Error('请选择文件')
  }

  const formData = new FormData()
  formData.append('image', file)

  const headers = {}
  if (token) {
    headers.token = token
  }

  const response = await fetch(`${BASE_URL}/admin/file/upload`, {
    method: 'POST',
    headers,
    body: formData
  })

  if (response.status === 401) {
    localStorage.removeItem('token')
    localStorage.removeItem('role')
    localStorage.removeItem('status')
    localStorage.removeItem('username')
    Message.warning('登录已过期，请重新登录')
    router.push('/login')
    throw new Error('登录已过期')
  }

  const result = await response.json()
  if (result.code === 200 || result.code === 1) {
    return result.data
  }
  throw new Error(result.msg || '上传失败')
}

// 图片资源基础路径
export const IMG_BASE_URL = ''